import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "page-view" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "tit-h2" }
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "btn-group" }
const _hoisted_6 = { class: "section" }

import {ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { RequestTypeKeys } from "@/types/constants";
import { RequestInfo } from '@/types/Request';
import { useRequestStore } from '@/stores/request';
import { getRequestInfo, removeRequest, modifyRequest } from "@/api";
import { ElMessageBox, ElNotification, ElMessage } from 'element-plus';
import CommonLoader from '@/components/CommonLoader.vue';
import ViewDetail from "@/components/ViewDetail.vue";
import HistoryList from '@/components/HistoryList.vue';
import HistoryImageViewer from "@/components/HistoryImageViewer.vue";
import StatusTag from '@/components/StatusTag.vue';
import IconBeus from '@/assets/images/ico-beus.svg';
import IconBi from '@/assets/images/ico-bi.svg';
import IconPV from '@/assets/images/ico-pv.svg';
import IconJam from '@/assets/images/ico-jam.svg';
import IconService from '@/assets/images/ico-service.svg';
import {useAuthorizedUsers} from "@/composables/useAuthorizedUsers";

interface RequestDetailCondition {
  requestType: RequestTypeKeys;
  beusPlanNumber: number | null;
  baPlanNumber: number | null;
}

interface Icon {
  condition: (detail: RequestDetailCondition) => boolean;
  src: string;
  alt: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewPage',
  props: {
    id: {}
  },
  setup(__props: any) {

const router = useRouter();

const request = ref<RequestInfo | null>(null);
const isLoading = ref<boolean>(false);
const requestStore = useRequestStore();
const { isAuthorized } = useAuthorizedUsers()

const props = __props;

const fetchData = async (id: string) => {
  isLoading.value = true;
  const params = {
    requestId: id,
  }
  // console.log('params',params );
  try {
    const response = await getRequestInfo(params);
    // console.log('Reqeust Info', response.result);
    if(response.result) {
      request.value = response.result;
      requestStore.setRequestIds(id, null);
      requestStore.setRequestData(response.result);
    }
  } catch (error) {
    console.error('Error Message:', error);
    ElMessageBox.alert('데이터를 가져올 수 없습니다. 대시보드로 이동합니다.', {
      confirmButtonText: '확인',
      callback: () => {
        router.push("/request");
      },
    });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fetchData(props.id);
});

const isCopyTooltipVisible = ref(false);
const copyUserId = async(text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    isCopyTooltipVisible.value = true;
    setTimeout(() => {
      isCopyTooltipVisible.value = false;
    }, 1000);
  } catch (error) {
    console.error('텍스트 복사 실패:', error);
    ElMessage.error('아이디 복사를 실패했습니다. 다시 시도해주세요');
  }
}

const icons: Icon[] = [
  {
    condition: ({ requestType, beusPlanNumber }: RequestDetailCondition) => 
      requestType === 'PLAN' && !!beusPlanNumber,
    src: IconBeus,
    alt: '플랜-히트맵',
  },
  {
    condition: ({ requestType, baPlanNumber }: RequestDetailCondition) => 
      requestType === 'PLAN' && !!baPlanNumber,
    src: IconBi,
    alt: '플랜-저니맵',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'PV_CHARGE',
    src: IconPV,
    alt: 'PV 충전',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'JAM_CHARGE',
    src: IconJam,
    alt: 'JAM 충전',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'ADDITIONAL',
    src: IconService,
    alt: '부가서비스',
  },
];

const getFilteredIcons = (request: RequestInfo): Icon[] => {
  const detail = {
    requestType: request.detail.requestType,
    baPlanNumber: request.detail.baPlanNumber,
    beusPlanNumber: request.detail.beusPlanNumber
  }
  return icons.filter((icon) => icon.condition(detail));
};

const requiredLogTypes = [
  'CREATED_EAGLE_PLAN',
  'CREATED_EAGLE_PV',
  'CREATED_EAGLE_JAM',
  'PROCESSED_REQUEST_ADDITIONAL',
];

const isModifiable = computed(() => {
  const hasRequiredLog = requiredLogTypes.some((logType) => {
    const exists = request.value?.history.some((item) => item.logType === logType);
    return exists;
  });

  if (!hasRequiredLog) {
    return true;
  }

  const isBeforeToday = request.value ? new Date(request.value.detail.useStartDate) > new Date() : false;

  if(request.value?.history.some((item) => item.logType === 'CREATED_EAGLE_PLAN')) return isBeforeToday;
  else return false;
});

const handleModifyRequest = () => router.push(`/request/modify/${props.id}`);
// const handleModifyRequest = () => router.push({ name: 'Modify', query: {
//   request: request.value
// }});

const isDeletable = computed(() => {
  const hasRequiredLog = requiredLogTypes.some((logType) => {
    const exists = request.value?.history.some((item) => item.logType === logType);
    return exists;
  });

  if (!hasRequiredLog) {
    return true;
  }

  const isBeforeToday = request.value ? new Date(request.value.detail.useStartDate) > new Date() : false;
  return isBeforeToday;
});

const handleDeleteRequest = async () => {
  try {
    await ElMessageBox.confirm(
      '리퀘스트를 정말 삭제하시겠습니까?<br/>삭제한 리퀘스트는 복구할 수 없습니다.',
      '경고',
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '확인',
        cancelButtonText: '취소',
      }
    );
    
    const params = { requestId: props.id }
    const response = await removeRequest(params);

    if(response.success) {
      ElMessage({
        message: '삭제가 완료되었습니다.',
        type: 'success',
      });
      router.push({name: 'Board'});
    }

  } catch (error) {
    if (error === 'cancel') {
      return;
    }
    console.error('Error messege:', error);
    ElMessage({
      message: '삭제 중 오류가 발생했습니다.',
      type: 'error',
    });
  }
}

const showImageViewer = ref(false);
const currentImage = ref('');
const openImageViewer = (url = '') => {
  currentImage.value = url;
  showImageViewer.value = true;
};
const closeImageViewer = () => {
  showImageViewer.value = false;
};

const toCurrentPlanList = () => router.push({ path: '/request/plan', query: { param: request.value?.customer.customerId } });
const toCurrentPaymentList = () => router.push({ path: '/request/payment', query: { param: request.value?.customer.customerId } });

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (request.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("header", _hoisted_2, [
              _createElementVNode("h2", _hoisted_3, _toDisplayString(request.value.customer.companyName), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredIcons(request.value), (icon, index) => {
                return (_openBlock(), _createElementBlock("img", {
                  key: index,
                  src: icon.src,
                  alt: icon.alt
                }, null, 8, _hoisted_4))
              }), 128)),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_el_button, {
                  type: "primary",
                  size: "large",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).go(-1)))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-arrow-left" }),
                    _cache[1] || (_cache[1] = _createTextVNode(" 뒤로가기 "))
                  ]),
                  _: 1
                }),
                (_unref(isAuthorized))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      size: "large",
                      type: "primary",
                      class: "btn-edit",
                      onClick: handleModifyRequest
                    }, {
                      default: _withCtx(() => _cache[2] || (_cache[2] = [
                        _createTextVNode(" 수정 ")
                      ])),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (request.value.isDeletable)
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 1,
                      size: "large",
                      type: "primary",
                      class: "btn-edit",
                      onClick: handleDeleteRequest
                    }, {
                      default: _withCtx(() => _cache[3] || (_cache[3] = [
                        _createTextVNode(" 삭제 ")
                      ])),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (request.value.detail.requestType === 'PLAN')
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 2,
                      type: "primary",
                      size: "large",
                      onClick: toCurrentPlanList
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-table-list" }),
                        _cache[4] || (_cache[4] = _createTextVNode(" 플랜 "))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_button, {
                  type: "primary",
                  size: "large",
                  onClick: toCurrentPaymentList
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, { icon: "fa-solid fa-table-list" }),
                    _cache[5] || (_cache[5] = _createTextVNode(" 결제 "))
                  ]),
                  _: 1
                })
              ])
            ]),
            _createVNode(ViewDetail, { data: request.value }, null, 8, ["data"]),
            _createElementVNode("section", _hoisted_6, [
              _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "tit-h3" }, " 히스토리 ", -1)),
              _createVNode(HistoryList, {
                data: request.value,
                onOpenImageViewer: openImageViewer
              }, null, 8, ["data"])
            ])
          ], 64))
        : (_openBlock(), _createBlock(CommonLoader, { key: 1 }))
    ]),
    (showImageViewer.value)
      ? (_openBlock(), _createBlock(HistoryImageViewer, {
          key: 0,
          url: currentImage.value,
          onClose: closeImageViewer
        }, null, 8, ["url"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})